import { Link } from "react-router-dom";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { Wordmark } from "@/components/Brand";

// Keep in step with the crawler copy in public/index.html.
const ABOUT = [
    {
        k: "pages",
        t: "What goes on a creator page",
        d: "Each page is written from the creator's own public posts: what they do, their services and prices where they publish them, the places they cover, recent posts, and links to everywhere they post. Pages update as the creator posts. They never show phone numbers or email addresses.",
    },
    {
        k: "answers",
        t: "Answers to local questions",
        d: "People ask AI specific things, like where to find all-you-can-eat sushi in Calgary or how to loosen tight hips from desk work in Toronto. Samoony publishes a creator's guide to questions like these, built only from places and products they have posted about, with each item quoting one of their posts.",
    },
    {
        k: "who",
        t: "Who it's for",
        d: "Local creators people follow and book: food and drink reviewers, makeup artists, skin and wellness practitioners, and family and travel creators. Most have under 10,000 followers, which is exactly who AI answers tend to miss.",
    },
    {
        k: "cities",
        t: "Where we are",
        d: "Samoony creators are in Calgary, Toronto and Seattle, with more cities to come. Each city has guides to the local creators worth knowing.",
    },
];

const STEPS = [
    {
        k: "01",
        t: "Add your handles.",
        d: "Instagram, TikTok, YouTube. Connect once.",
    },
    {
        k: "02",
        t: "Approve your page.",
        d: "Nothing goes live without your okay.",
    },
    {
        k: "03",
        t: "Get named.",
        d: "We keep you current everywhere AI looks. You don't lift a finger.",
    },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-paper text-ink flex flex-col">
            <header className="flex items-center justify-between px-5 py-5 sm:px-10 sm:py-7">
                <Link to="/" className="no-underline">
                    <Wordmark height={28} />
                </Link>
                <SignedIn>
                    <UserButton afterSignOutUrl="/" />
                </SignedIn>
                <SignedOut>
                    <Link
                        data-testid="landing-signin-link"
                        to="/sign-in"
                        className="samoony-label hover:text-ink transition-colors"
                    >
                        Sign in
                    </Link>
                </SignedOut>
            </header>

            <main className="flex-1 px-5 sm:px-10 py-10 flex flex-col justify-center">
                <div className="max-w-3xl animate-fade-up">
                    <h1
                        className="samoony-hero text-ink mb-8"
                        style={{
                            fontSize: "clamp(76px, 18vw, 180px)",
                            fontStretch: "68%",
                            lineHeight: "0.86",
                        }}
                    >
                        Get <span className="text-signal">named</span>.
                    </h1>
                    <p className="text-lg sm:text-xl text-ink-soft max-w-xl mb-10">
                        When people ask AI for the top creators in your niche,
                        be the name that comes up.
                    </p>
                    <p className="text-base text-ink-soft max-w-xl mb-10">
                        Samoony builds a public page for each local creator, from
                        Calgary food reviewers and bridal makeup artists to Toronto
                        bodywork practitioners, and features them in city guides
                        that search engines and AI assistants read.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <SignedOut>
                            <Link
                                data-testid="landing-cta-signup"
                                to="/sign-up"
                                className="samoony-pill samoony-pill-primary"
                            >
                                Create your profile
                            </Link>
                            <Link
                                data-testid="landing-cta-signin"
                                to="/sign-in"
                                className="samoony-pill samoony-pill-ghost"
                            >
                                I already have one
                            </Link>
                        </SignedOut>
                        <SignedIn>
                            <Link
                                data-testid="landing-cta-continue"
                                to="/profile"
                                className="samoony-pill samoony-pill-primary"
                            >
                                Go to your profile
                            </Link>
                        </SignedIn>
                    </div>
                </div>

                <div
                    data-testid="landing-steps"
                    className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl"
                >
                    {STEPS.map((row) => (
                        <div
                            key={row.k}
                            data-testid={`landing-step-${row.k}`}
                            className="samoony-card p-6"
                        >
                            <div className="samoony-label mb-3">{row.k}</div>
                            <div
                                className="font-display font-extrabold text-xl mb-2 leading-tight"
                                style={{ fontStretch: "80%" }}
                            >
                                {row.t}
                            </div>
                            <div className="text-sm text-ink-soft">
                                {row.d}
                            </div>
                        </div>
                    ))}
                </div>

                <div
                    data-testid="landing-about"
                    className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl"
                >
                    {ABOUT.map((row) => (
                        <section
                            key={row.k}
                            data-testid={`landing-about-${row.k}`}
                            className="samoony-card p-6"
                        >
                            <h2
                                className="font-display font-extrabold text-xl mb-2 leading-tight"
                                style={{ fontStretch: "80%" }}
                            >
                                {row.t}
                            </h2>
                            <p className="text-sm text-ink-soft">{row.d}</p>
                        </section>
                    ))}
                </div>

                <section
                    data-testid="landing-accounts"
                    className="mt-4 max-w-3xl samoony-card p-6"
                >
                    <div
                        className="font-display font-extrabold text-xl mb-2 leading-tight"
                        style={{ fontStretch: "80%" }}
                    >
                        Your accounts stay yours
                    </div>
                    <div className="text-sm text-ink-soft">
                        We only read your public posts. We never ask for your
                        password or post as you. Nothing goes live without
                        your okay, and you can take your page down at any
                        time.
                    </div>
                </section>

                <section
                    data-testid="landing-guides"
                    className="mt-4 max-w-3xl samoony-card p-6"
                >
                    <div
                        className="font-display font-extrabold text-xl mb-2 leading-tight"
                        style={{ fontStretch: "80%" }}
                    >
                        Creator guides
                    </div>
                    <div className="text-sm text-ink-soft">
                        Local creators to know, by city and niche.{" "}
                        <a
                            href="/guides"
                            data-testid="landing-guides-link"
                            className="text-ink hover:underline decoration-signal underline-offset-2"
                        >
                            Browse the creator guides
                        </a>
                        .
                    </div>
                </section>

                <section
                    data-testid="landing-research"
                    className="mt-4 max-w-3xl samoony-card p-6"
                >
                    <div
                        className="font-display font-extrabold text-xl mb-2 leading-tight"
                        style={{ fontStretch: "80%" }}
                    >
                        What AI looks for when it names a local creator
                    </div>
                    <div className="text-sm text-ink-soft">
                        Findings from an OmniCite study of 686 AI answers about local creators in
                        Calgary, Seattle and Toronto.{" "}
                        <a
                            href="/articles/what-ai-looks-for-when-it-names-a-local-creator"
                            data-testid="landing-research-link"
                            className="text-ink hover:underline decoration-signal underline-offset-2"
                        >
                            Read the article
                        </a>
                        .
                    </div>
                </section>
            </main>

            <footer
                className="px-5 sm:px-10 py-6 samoony-label flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            >
                <span data-testid="landing-footer-contact">
                    Samoony · Calgary, Alberta ·{" "}
                    <a
                        data-testid="landing-footer-email"
                        href="mailto:getsamoony@gmail.com"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        getsamoony@gmail.com
                    </a>{" "}
                    · A product of{" "}
                    <a
                        data-testid="landing-footer-omnicite"
                        href="https://omnicite.ai/"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        OmniCite Inc.
                    </a>
                </span>
                <nav className="flex items-center gap-4" data-testid="landing-footer-nav">
                    <a
                        href="/guides"
                        data-testid="landing-footer-guides"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        Creator guides
                    </a>
                    <a
                        href="/articles"
                        data-testid="landing-footer-articles"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        Articles
                    </a>
                    <a
                        href="/about"
                        data-testid="landing-footer-about"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        About
                    </a>
                    <a
                        href="/privacy"
                        data-testid="landing-footer-privacy"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        Privacy
                    </a>
                    <a
                        href="/terms"
                        data-testid="landing-footer-terms"
                        className="hover:underline decoration-signal underline-offset-2"
                    >
                        Terms
                    </a>
                </nav>
            </footer>
        </div>
    );
}
